import map from 'lodash/map';

const createListOptions = rawList =>
  map(rawList, (label, value) => ({
    label,
    value,
  }));

const DEFAULT_LANGUAGES = Object.freeze({
  en: 'English',
  'en-gb': 'English, UK',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
});

export const DEFAULT_LANGUAGES_OPTIONS = createListOptions(DEFAULT_LANGUAGES);

const ALICE_LANGUAGES = Object.freeze({
  'da-DK': 'Danish, Denmark',
  'de-DE': 'German, Germany',
  'en-AU': 'English, Australia',
  'en-CA': 'English, Canada',
  'en-GB': 'English, UK',
  'en-IN': 'English, India',
  'en-US': 'English, United States',
  'ca-ES': 'Catalan, Spain',
  'es-ES': 'Spanish, Spain',
  'es-MX': 'Spanish, Mexico',
  'fi-FI': 'Finnish, Finland',
  'fr-CA': 'French, Canada',
  'fr-FR': 'French, France',
  'it-IT': 'Italian, Italy',
  'ja-JP': 'Japanese, Japan',
  'ko-KR': 'Korean, Korea ',
  'nb-NO': 'Norwegian, Norway',
  'nl-NL': 'Dutch, Netherlands',
  'pl-PL': 'Polish-Poland',
  'pt-BR': 'Portuguese, Brazil',
  'pt-PT': 'Portuguese, Portugal ',
  'ru-RU': 'Russian, Russia',
  'sv-SE': 'Swedish, Sweden',
  'zh-CN': 'Chinese (Mandarin)',
  'zh-HK': 'Chinese (Cantonese)',
  'zh-TW': 'Chinese (Taiwanese Mandarin)',
});

export const ALICE_LANGUAGE_OPTIONS = createListOptions(ALICE_LANGUAGES);

export const SUPPORTED_VOICES = Object.freeze({
  MAN_VOICE: 'man',
  WOMAN_VOICE: 'woman',
  ALICE_VOICE: 'alice',
  CUSTOM: 'custom',
});

export const VOICES_OPTIONS = Object.freeze([
  { label: 'Man', value: SUPPORTED_VOICES.MAN_VOICE },
  { label: 'Woman', value: SUPPORTED_VOICES.WOMAN_VOICE },
  { label: 'Alice', value: SUPPORTED_VOICES.ALICE_VOICE },
  { label: 'Custom', value: SUPPORTED_VOICES.CUSTOM },
]);

const DEFAULT_LANGUAGE_OPTION = { label: 'English', value: 'en' };
const DEFAULT_ALICE_LANGUAGE_OPTION = { label: 'English, United States', value: 'en-US' };

export const VOICE_LANGUAGES_MAPPER = Object.freeze({
  [SUPPORTED_VOICES.MAN_VOICE]: {
    languageOptions: DEFAULT_LANGUAGES_OPTIONS,
    defaultLanguage: DEFAULT_LANGUAGE_OPTION,
  },
  [SUPPORTED_VOICES.WOMAN_VOICE]: {
    languageOptions: DEFAULT_LANGUAGES_OPTIONS,
    defaultLanguage: DEFAULT_LANGUAGE_OPTION,
  },
  [SUPPORTED_VOICES.ALICE_VOICE]: {
    languageOptions: ALICE_LANGUAGE_OPTIONS,
    defaultLanguage: DEFAULT_ALICE_LANGUAGE_OPTION,
  },
  [SUPPORTED_VOICES.CUSTOM]: {
    languageOptions: DEFAULT_LANGUAGES_OPTIONS,
    defaultLanguage: DEFAULT_LANGUAGE_OPTION,
  },
});

export const DEFAULT_VOICE = SUPPORTED_VOICES.MAN_VOICE;

const TWILIO_SUPPORTED_LANGUAGES = Object.freeze({
  'af-ZA': 'Afrikaans (South Africa)',
  'am-ET': 'Amharic (Ethiopia)',
  'ar-AE': 'Arabic (United Arab Emirates)',
  'ar-BH': 'Arabic (Bahrain)',
  'ar-DZ': 'Arabic (Algeria)',
  'ar-EG': 'Arabic (Egypt)',
  'ar-IL': 'Arabic (Israel)',
  'ar-IQ': 'Arabic (Iraq)',
  'ar-JO': 'Arabic (Jordan)',
  'ar-KW': 'Arabic (Kuwait)',
  'ar-LB': 'Arabic (Lebanon)',
  'ar-MA': 'Arabic (Morocco)',
  'ar-OM': 'Arabic (Oman)',
  'ar-PS': 'Arabic (State of Palestine)',
  'ar-QA': 'Arabic (Qatar)',
  'ar-SA': 'Arabic (Saudi Arabia)',
  'ar-TN': 'Arabic (Tunisia)',
  'az-AZ': 'Azerbaijani (Azerbaijani)',
  'bg-BG': 'Bulgarian (Bulgaria)',
  'bn-BD': 'Bengali (Bangladesh)',
  'bn-IN': 'Bengali (India)',
  'ca-ES': 'Catalan (Spain)',
  'cmn-Hans-CN': 'Chinese, Mandarin (Simplified, China)',
  'cmn-Hans-HK': 'Chinese, Mandarin (Simplified, Hong Kong)',
  'cmn-Hant-TW': 'Chinese, Mandarin (Traditional, Taiwan)',
  'cs-CZ': 'Czech (Czech Republic)',
  'da-DK': 'Danish (Denmark)',
  'de-DE': 'German (Germany)',
  'el-GR': 'Greek (Greece)',
  'en-AU': 'English (Australia)',
  'en-CA': 'English (Canada)',
  'en-GB': 'English (United Kingdom)',
  'en-GH': 'English (Ghana)',
  'en-IE': 'English (Ireland)',
  'en-IN': 'English (India)',
  'en-KE': 'English (Kenya)',
  'en-NG': 'English (Nigeria)',
  'en-NZ': 'English (New Zealand)',
  'en-PH': 'English (Philippines)',
  'en-TZ': 'English (Tanzania)',
  'en-US': 'English (United States)',
  'en-ZA': 'English (South Africa)',
  'es-AR': 'Spanish (Argentina)',
  'es-BO': 'Spanish (Bolivia)',
  'es-CL': 'Spanish (Chile)',
  'es-CO': 'Spanish (Colombia)',
  'es-CR': 'Spanish (Costa Rica)',
  'es-DO': 'Spanish (Dominican Republic)',
  'es-EC': 'Spanish (Ecuador)',
  'es-ES': 'Spanish (Spain)',
  'es-GT': 'Spanish (Guatemala)',
  'es-HN': 'Spanish (Honduras)',
  'es-MX': 'Spanish (Mexico)',
  'es-NI': 'Spanish (Nicaragua)',
  'es-PA': 'Spanish (Panama)',
  'es-PE': 'Spanish (Peru)',
  'es-PR': 'Spanish (Puerto Rico)',
  'es-PY': 'Spanish (Paraguay)',
  'es-SV': 'Spanish (El Salvador)',
  'es-US': 'Spanish (United States)',
  'es-UY': 'Spanish (Uruguay)',
  'es-VE': 'Spanish (Venezuela)',
  'eu-ES': 'Basque (Spain)',
  'fa-IR': 'Persian (Iran)',
  'fi-FI': 'Finnish (Finland)',
  'fil-PH': 'Filipino (Philippines)',
  'fr-CA': 'French (Canada)',
  'fr-FR': 'French (France)',
  'gl-ES': 'Galician (Spain)',
  'gu-IN': 'Gujarati (India)',
  'he-IL': 'Hebrew (Israel)',
  'hi-IN': 'Hindi (India)',
  'hr-HR': 'Croatian (Croatia)',
  'hu-HU': 'Hungarian (Hungary)',
  'hy-AM': 'Armenian (Armenia)',
  'id-ID': 'Indonesian (Indonesia)',
  'is-IS': 'Icelandic (Iceland)',
  'it-IT': 'Italian (Italy)',
  'ja-JP': 'Japanese (Japan)',
  'jv-ID': 'Javanese (Indonesia)',
  'ka-GE': 'Georgian (Georgia)',
  'km-KH': 'Khmer (Cambodian)',
  'kn-IN': 'Kannada (India)',
  'ko-KR': 'Korean (South Korea)',
  'lo-LA': 'Lao (Laos)',
  'lt-LT': 'Lithuanian (Lithuania)',
  'lv-LV': 'Latvian (Latvia)',
  'ml-IN': 'Malayalam (India)',
  'mr-IN': 'Marathi (India)',
  'ms-MY': 'Malay (Malaysia)',
  'nb-NO': 'Norwegian Bokmål (Norway)',
  'ne-NP': 'Nepali (Nepal)',
  'nl-NL': 'Dutch (Netherlands)',
  'pl-PL': 'Polish (Poland)',
  'pt-BR': 'Portuguese (Brazil)',
  'pt-PT': 'Portuguese (Portugal)',
  'ro-RO': 'Romanian (Romania)',
  'ru-RU': 'Russian (Russia)',
  'si-LK': 'Sinhala (Sri Lanka)',
  'sk-SK': 'Slovak (Slovakia)',
  'sl-SI': 'Slovenian (Slovenia)',
  'sr-RS': 'Serbian (Serbia)',
  'su-ID': 'Sundanese (Indonesia)',
  'sv-SE': 'Swedish (Sweden)',
  'sw-KE': 'Swahili (Kenya)',
  'sw-TZ': 'Swahili (Tanzania)',
  'ta-IN': 'Tamil (India)',
  'ta-LK': 'Tamil (Sri Lanka)',
  'ta-MY': 'Tamil (Malaysia)',
  'ta-SG': 'Tamil (Singapore)',
  'te-IN': 'Telugu (India)',
  'th-TH': 'Thai (Thailand)',
  'tr-TR': 'Turkish (Turkey)',
  'uk-UA': 'Ukrainian (Ukraine)',
  'ur-IN': 'Urdu (India)',
  'ur-PK': 'Urdu (Pakistan)',
  'vi-VN': 'Vietnamese (Vietnam)',
  'yue-Hant-HK': 'Chinese, Cantonese (Traditional, Hong Kong)',
  'zu-ZA': 'Zulu (South Africa)',
});

export const TWILIO_LANGUAGE_OPTIONS = createListOptions(TWILIO_SUPPORTED_LANGUAGES);
